import { useState, useCallback, useEffect } from 'react';
import { useRealtimeEvent } from './useRealtimeEvent';
import {
  RealtimeEventType,
  CommentNewEvent,
  CommentUpdatedEvent,
  CommentDeletedEvent
} from '../../services/websocket';
import tripCommentService from '../../services/trips/trip-comment.service';
import {
  ITripComment,
  ICommentFilters
} from '../../Models/trips/trip-comment.model';

interface UseTripCommentsReturn {
  comments: ITripComment[];
  loading: boolean;
  error: string | null;
  addComment: (
    comment: string,
    entityType?: string,
    entityId?: string
  ) => Promise<void>;
  updateComment: (commentId: string, newComment: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing trip comments with real-time updates
 *
 * @param tripId - The ID of the trip
 * @param filters - Optional filters for entityType and entityId
 * @returns Comments array, loading state, error state, and action methods
 *
 * @example
 * // Get all comments for a trip
 * const { comments, addComment } = useTripComments(tripId);
 *
 * @example
 * // Get comments for a specific activity
 * const { comments } = useTripComments(tripId, {
 *   entityType: 'activity',
 *   entityId: activityId
 * });
 */
export const useTripComments = (
  tripId: string,
  filters?: ICommentFilters
): UseTripCommentsReturn => {
  const [comments, setComments] = useState<ITripComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stabilize filters by extracting primitive values
  const entityType = filters?.entityType;
  const entityId = filters?.entityId;

  /**
   * Fetch comments from server
   */
  const fetchComments = useCallback(async () => {
    if (!tripId) return;

    setLoading(true);
    setError(null);

    try {
      const fetchedComments = await tripCommentService.getComments(
        tripId,
        entityType || entityId ? { entityType, entityId } : undefined
      );
      setComments(fetchedComments);
    } catch (err: any) {
      console.error('Failed to fetch comments:', err);
      setError(err.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [tripId, entityType, entityId]);

  /**
   * Add a new comment
   */
  const addComment = useCallback(
    async (comment: string, entityType?: string, entityId?: string) => {
      if (!tripId) return;

      try {
        const newComment = await tripCommentService.addComment(tripId, {
          comment,
          entityType: (entityType || 'trip') as any,
          entityId
        });

        // Optimistic update - add to local state immediately
        // Real-time event will also fire, but this provides instant feedback
        setComments((prev) => [...prev, newComment]);
      } catch (err: any) {
        console.error('Failed to add comment:', err);
        setError(err.message || 'Failed to add comment');
        throw err;
      }
    },
    [tripId]
  );

  /**
   * Update an existing comment
   */
  const updateComment = useCallback(
    async (commentId: string, newComment: string) => {
      if (!tripId) return;

      try {
        const updatedComment = await tripCommentService.updateComment(
          tripId,
          commentId,
          { comment: newComment }
        );

        // Optimistic update
        setComments((prev) =>
          prev.map((c) =>
            c.id === commentId
              ? {
                  ...c,
                  comment: updatedComment.comment,
                  updatedAt: updatedComment.updatedAt
                }
              : c
          )
        );
      } catch (err: any) {
        console.error('Failed to update comment:', err);
        setError(err.message || 'Failed to update comment');
        throw err;
      }
    },
    [tripId]
  );

  /**
   * Delete a comment (soft delete)
   */
  const deleteComment = useCallback(
    async (commentId: string) => {
      if (!tripId) return;

      try {
        await tripCommentService.deleteComment(tripId, commentId);

        // Optimistic update - remove from local state
        setComments((prev) => prev.filter((c) => c.id !== commentId));
      } catch (err: any) {
        console.error('Failed to delete comment:', err);
        setError(err.message || 'Failed to delete comment');
        throw err;
      }
    },
    [tripId]
  );

  // ===============================
  // REAL-TIME WEBSOCKET HANDLERS
  // ===============================

  /**
   * Handle new comment event from WebSocket
   */
  useRealtimeEvent(
    RealtimeEventType.COMMENT_NEW,
    useCallback(
      (data: CommentNewEvent) => {
        // Only add if comment belongs to this trip
        if (data.tripId !== tripId) return;

        // Apply filters
        if (entityType && data.entityType !== entityType) return;
        if (entityId && data.entityId !== entityId) return;

        // Check if comment already exists (avoid duplicates from optimistic update)
        setComments((prev) => {
          const exists = prev.some((c) => c.id === data.commentId);
          if (exists) return prev;

          // Add new comment
          const newComment: ITripComment = {
            id: data.commentId,
            entityType: (data.entityType || 'trip') as any,
            entityId: data.entityId,
            userId: data.authorId,
            comment: data.content,
            createdAt: new Date(data.createdAt).toISOString(),
            isActive: true,
            user: {
              username: data.authorUsername,
              photo: data.authorPhoto
            }
          };

          return [...prev, newComment];
        });
      },
      [tripId, entityType, entityId]
    )
  );

  /**
   * Handle comment updated event from WebSocket
   */
  useRealtimeEvent(
    RealtimeEventType.COMMENT_UPDATED,
    useCallback(
      (data: CommentUpdatedEvent) => {
        // Only update if comment belongs to this trip
        if (data.tripId !== tripId) return;

        setComments((prev) =>
          prev.map((c) =>
            c.id === data.commentId
              ? {
                  ...c,
                  comment: data.content,
                  updatedAt: new Date(data.updatedAt).toISOString()
                }
              : c
          )
        );
      },
      [tripId]
    )
  );

  /**
   * Handle comment deleted event from WebSocket
   */
  useRealtimeEvent(
    RealtimeEventType.COMMENT_DELETED,
    useCallback(
      (data: CommentDeletedEvent) => {
        // Only delete if comment belongs to this trip
        if (data.tripId !== tripId) return;

        setComments((prev) => prev.filter((c) => c.id !== data.commentId));
      },
      [tripId]
    )
  );

  // ===============================
  // INITIAL FETCH
  // ===============================

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return {
    comments,
    loading,
    error,
    addComment,
    updateComment,
    deleteComment,
    refresh: fetchComments
  };
};
